import { Component, OnInit, Input, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import { SecurityService } from '../../../../services/security.service';
import { Subject } from "rxjs";
import { ago, daysAgo } from 'src/app/utils/date';
import moment from 'moment';
import { paymentCategoryIcon } from 'src/app/utils/icons';
import { Receivable } from 'src/app/models/receivable.model';
import { AutomationEntry } from 'src/app/models/automation-entry.model';

@Component({
    selector: 'pays',
    templateUrl: './pays.component.html',
    styleUrls: ['./pays.component.scss'],
    standalone: false
})
export class PaysComponent implements OnInit {

    // Unique internal identity of the contact whose soon due payments will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // A payment was taken so lets update our available payments and any suppporting displays.
    @Input() paymentTaken: Subject<Object> | null = null

    // Communicate payment updates to the parent component.
    @Output() paysChanged: EventEmitter<Object> = new EventEmitter()

    //
    protected payments: Array<Receivable> = []

    // Groups of related payments so we can build tabs.
    protected groups: Object = {}

    //
    private records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    // The vehicle selected for the gps device list.
    protected vehicleId: number = 0

    // The current selected receivable to payoff or null
    protected payoffReceivable: Receivable | null | undefined = null

    // Basic information to recompute a receivable.
    protected entry: AutomationEntry | null = null

    // To recompute a specific term (more comprehensive) set this identity.
    protected entryTermsId: number = 0

    // A handle to a payment taken subscription so we can unsubscribe later.
    private paymentTakenWatcher: any = null

    get groupKeys() { return Object.keys(this.groups) }

    constructor (private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, protected security: SecurityService) {}

    ngOnInit() {
        this.fetch()

        this.paymentTakenWatcher = this.paymentTaken.subscribe((payment) => {
            this.reset()
            this.fetch()
        })
    }

    ngOnDestroy() {
        if (this.paymentTakenWatcher) {
            this.paymentTakenWatcher.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.contactId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    /** 
     * Fetch the the pending payments for the current contact.
    */
    private fetch = (): void => {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
        }

        this.loading = true
        if (this.page === 1) {
            this.payments = []
        }
        
        this.master.get(`receivables?page=${this.page}${contactParam}&quotes=true`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.payments = this.payments.concat(res.data.receivables)
                this.constructGroups()
                return
            }
  
            this.payments = res.data.receivables
            this.constructGroups()
        })
    }

    /**
     * Groups payments into their respective categories and 
     * loans for the tabbed display in the UI.
     */
    private constructGroups = () => {
        const pays = {
            past: {
                total: 0,
                items: []
            },
            today: {
                total: 0,
                items: []
            },
            next: {
                total: 0,
                items: []
            }
        }
        
        if (!this.payments || (this.payments.length <= 0)) {
            this.paysChanged.emit(pays)
            this.groups = {}
            return
        }
        
        if (this.page <= 1) {
            this.groups = {}
        }

        for (const payment of this.payments) {
            const key = `${payment.saleId || 0}-${payment.serviceId || 0}-${payment.insuranceId || 0}`
            if (!this.groups[key]) {
                this.groups[key] = {}
                this.groups[key].id = payment.id
                this.groups[key].payments = []
                this.groups[key].termsId = payment.termsId
                this.groups[key].terms = null
                this.groups[key].url = payment.url
                this.groups[key].vehicleId = payment.vehicleId
                this.groups[key].title = `${payment.contact}`
                this.groups[key].subtitle = ''
                this.groups[key].method = payment.method
                this.groups[key].sold = payment.sold
                this.groups[key].payoff = payment.payoff
                this.groups[key].balance = payment.balance
                this.groups[key].paymentBalance = payment.paymentBalance
                this.groups[key].tax = payment.taxBalance
                this.groups[key].apr = payment.apr
                this.groups[key].payment = payment.payment
                this.groups[key].unearnedInterest = payment.unearnedInterest
                this.groups[key].unpaidEarnedInterest = payment.unpaidEarnedInterest
                this.groups[key].saleId = payment.saleId
                this.groups[key].type = payment.type
                this.groups[key].financed = (payment.financed == true)
                this.groups[key].bhph = (payment.bhph == true)
                this.groups[key].quote = (payment.quote == true)
                this.groups[key].ridingCash = payment.ridingCash
                this.groups[key].lateFees = payment.lateFees
                this.groups[key].waived = (payment.lateFeesWaived > 0)
                this.groups[key].gpsCount = payment.gpsCount
                this.groups[key].cpiEnds = payment.cpiEnds
                this.groups[key].insuranceProvider = payment.insuranceProvider
                this.groups[key].insuranceExpires = payment.insuranceExpires
                this.groups[key].frequency = payment.frequency
                this.groups[key].category = payment.category
                this.groups[key].lienholder = payment.lienholder
                this.groups[key].percentPrincipalPaid = payment.percentPrincipalPaid

                if (payment.vin) {
                    this.groups[key].title = `${payment.vehicle}`
                    this.groups[key].subtitle = `${payment.stock} | ${payment.vin}`
                }
            }

            if (payment.deposit > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.depositDue,
                    category: 'deposit',
                    lienholder: null,
                    amount: payment.deposit,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.initial > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.initialDue,
                    category: 'initial down',
                    lienholder: null,
                    amount: payment.initial,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.deferred > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.deferredDue,
                    category: 'deferred down',
                    lienholder: null,
                    amount: payment.deferred,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (!payment.quote) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.loanDue || payment.due,
                    category: payment.bhph ? 'mv loan' : 'drive-out price',
                    lienholder: payment.bhph ? null : payment.lienholder,
                    amount: payment.loan,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if ((payment.lateFees > 0) || (payment.lateFeesWaived > 0)) {
                const today = moment().startOf('day').format('yyyy-MM-DD')
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.lateFeesDue || today,
                    category: 'late fee',
                    lienholder: null,
                    amount: payment.lateFees,
                    waived: payment.lateFeesWaived,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.bounceFees > 0) {
                const today = moment().startOf('day').format('yyyy-MM-DD')
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.bounceFeesDue || today,
                    category: 'bounce',
                    lienholder: null,
                    amount: payment.bounceFees,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.cpi > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.cpiDue,
                    category: 'cpi',
                    lienholder: null,
                    amount: payment.cpi,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            for (const item of this.groups[key].payments) {
                const timeline = ago(item.due)
                const cloned = JSON.parse(JSON.stringify(item))

                switch (timeline) {
                    case 'past':
                        pays.past.total += parseFloat(item.amount)
                        pays.past.items.push(cloned)

                        pays.today.total += parseFloat(item.amount)
                        pays.today.items.push(cloned)
                        break
                    case 'today':
                        pays.today.total += parseFloat(item.amount)
                        pays.today.items.push(cloned)
                        break
                    default: // soon (next)
                        pays.next.total += parseFloat(item.amount)
                        pays.next.items.push(cloned)
                        break
                }
            }
        }

        this.paysChanged.emit(pays)
    }

    /**
     * Determine an tab icon to show based on the payment category.
     * @param category The payment category.
     */
    protected icon = (category: string) => {
        return paymentCategoryIcon(category)
    }

    protected dueToClass = (due: string): string => {
        return ago(due)
    }

    protected daysPastDue = (due: string): number => {
        return daysAgo(due)
    }

    protected insuranceMessage = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'No insurance'
        }

        if (receivable.insuranceExpires && receivable.insuranceIsCpi) {
            return this.cpiMessage(receivable.insuranceExpires)
        }

        return this.insMessage(receivable.insuranceProvider, receivable.insuranceExpires)
    }

    protected insuranceClass = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'danger'
        }

        return this.insClass(receivable.insuranceExpires, receivable.insuranceWarnDays)
    }

    private cpiMessage = (ends: string): string => {
        if (!ends) {
            return 'No CPI'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `CPI expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `CPI expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return 'CPI in good standing'
    }

    private insClass = (ends: string, warnDays: number = 15): string => {
        if (!ends) {
            return 'secondary'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return 'danger'
        }

        if (days <= warnDays) {
            return 'warning'
        }

        return 'success'
    }

    private insMessage = (provider: string, ends: string): string => {
        if (!ends) {
            return 'No insurance'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `Insurance expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `Insurance expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return `${provider} active`
    }

    /**
     * Open a list of GPS devices for the given vehicle.
     * @param vehicleId Unique internal identity of the vehicle whose gps devices will be listed.
     */
    protected showDevices = (vehicleId: number): void => {
        this.vehicleId = vehicleId

        const btn = document.getElementById('toggleGpsDeviceView') as HTMLButtonElement
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
    *
    */
    protected recompute = (e: Event, termsId: number): void => {
        if (!this.payments || (this.payments.length <= 0)) {
            return
        }

        const receivable = this.payments.find((item) => item.termsId == termsId)
        if (!receivable) {
            return
        }

        const entry = new AutomationEntry()
        entry.lotId = receivable.lotId
        entry.jobType = 'computeTerms'
        entry.corporateId = receivable.corporateId
        entry.asOf = moment(receivable.day).format('YYYY-MM-DD')

        this.entry = entry
        this.entryTermsId = receivable.termsId

        const btn = (document.getElementById("btn-toggle-run-receivable-view") as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Indicates the user chose to recompute the receivables and it was successful.
     * @param meta 
     */
    protected recomputed = (meta: Object) => {
        const btn = (document.getElementById("btn-toggle-run-receivable-view") as HTMLButtonElement)
        if (btn) {
            btn.click()
        }

        this.entry = null
        this.entryTermsId = 0

        this.reset()
        this.fetch()
    }

    /**
     * 
     * @param e 
     * @param termsId 
     */
    protected payoff = (e: Event, termsId: number) => {
        this.payoffReceivable = null

        if (!this.payments || (this.payments.length <= 0)) {
            return
        }

        this.payoffReceivable = this.payments.find((item) => item.termsId == termsId)
        if (!this.payoffReceivable) {
            return
        }

        const btn = document.getElementById('togglePayoffView') as HTMLButtonElement
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * The user chose to payoff the sale and it was successful.
     * @param details Payoff details the user inputted.
     */
    protected paidOff = (details: Object) => {
        this.reset()
        this.fetch()

        const btn = document.getElementById('togglePayoffView') as HTMLButtonElement
        if (!btn) {
            return
        }

        btn.click()
    }
}
